import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';

import type { RiftboundCard } from '../../types';

import { pageBg } from '../../theme/themeColor';
import { sanityCardArtUrl } from '../../utils/sanityCardArtUrl';
import { isLandscapeCardArt } from '../../utils/cardArt';

interface CardWallProps {
  cards: RiftboundCard[];
}

const getCardId = (card: RiftboundCard): string | null => card?.id || null;

function cardImageUrl(card: RiftboundCard): string {
  return (card.thumbnailUrl || card.imageUrl || '').trim();
}

const vignetteGradient = `radial-gradient(ellipse 60% 45% at 50% 45%, transparent 0%, ${pageBg(0.5)} 40%, ${pageBg(0.9)} 70%, ${pageBg(1)} 100%)`;
const topFadeGradient = `linear-gradient(to bottom, ${pageBg(1)} 0%, transparent 100%)`;
const bottomFadeGradient = `linear-gradient(to top, ${pageBg(1)} 0%, transparent 100%)`;

const COMPACT_WALL_MEDIA = '(max-width: 767px)';

const WALL_LAYOUTS = {
  full: {
    cols: 11,
    rows: 5,
    cardWidth: 130,
    imageWidth: 220,
    blur: true,
    float: true,
  },
  compact: {
    cols: 7,
    rows: 4,
    cardWidth: 105,
    imageWidth: 180,
    blur: false,
    float: false,
  },
} as const;

function getIsCompactWall(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(COMPACT_WALL_MEDIA).matches;
}

function deferDecorativeWork(callback: () => void): () => void {
  const win = window as Window & {
    requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (typeof win.requestIdleCallback === 'function') {
    const handle = win.requestIdleCallback(callback, { timeout: 1200 });
    return () => win.cancelIdleCallback?.(handle);
  }

  const handle = window.setTimeout(callback, 250);
  return () => window.clearTimeout(handle);
}

export function CardWall({ cards }: CardWallProps) {
  const outerRef = useRef<HTMLDivElement | null>(null);
  const planeRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [isCompact, setIsCompact] = useState(getIsCompactWall);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const layout = isCompact ? WALL_LAYOUTS.compact : WALL_LAYOUTS.full;

  useEffect(() => {
    return deferDecorativeWork(() => setReady(true));
  }, []);

  useEffect(() => {
    const media = window.matchMedia(COMPACT_WALL_MEDIA);
    const handleChange = () => setIsCompact(media.matches);

    handleChange();
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    setFailedImages(new Set());
  }, [cards]);

  const handleImageError = useCallback((cardId: string) => {
    setFailedImages((prev) => {
      const next = new Set(prev);
      next.add(cardId);
      return next;
    });
  }, []);

  useEffect(() => {
    let frame = 0;
    let pointerX = 0.5;
    let pointerY = 0.5;

    const updateWallPosition = () => {
      frame = 0;

      const x = pointerX - 0.5;
      const y = pointerY - 0.5;

      outerRef.current?.style.setProperty('--wall-offset-x', `${x * 50}px`);
      outerRef.current?.style.setProperty('--wall-offset-y', `${y * 25}px`);
      planeRef.current?.style.setProperty('--wall-rotate-x', `${20 + y * -5}deg`);
      planeRef.current?.style.setProperty('--wall-rotate-y', `${x * 8}deg`);
    };

    const handlePointerMove = (e: PointerEvent) => {
      pointerX = e.clientX / window.innerWidth;
      pointerY = e.clientY / window.innerHeight;

      if (!frame) {
        frame = requestAnimationFrame(updateWallPosition);
      }
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, []);

  const validCards = useMemo(() => {
    if (!ready) return [];

    return cards.filter((card) => {
      const cardId = getCardId(card);
      const src = cardImageUrl(card);
      return cardId && src && !failedImages.has(cardId) && !isLandscapeCardArt(card);
    });
  }, [cards, failedImages, ready]);

  const wallCards = useMemo(() => {
    if (!validCards.length) return [];

    const result: Array<{
      card: RiftboundCard;
      row: number;
      col: number;
      x: number;
      y: number;
      z: number;
      rotateX: number;
      rotateY: number;
      cardWidth: number;
      cardHeight: number;
    }> = [];

    const { cols, rows, cardWidth } = layout;
    const cardAspectRatio = 48 / 67;
    const cardHeight = cardWidth / cardAspectRatio;
    const rowHeight = cardHeight;

    let cardIndex = 0;

    for (let row = 0; row < rows; row++) {
      const rowOffsetPx = row % 2 === 0 ? 0 : cardWidth / 2;
      const rowDistFromCenter = Math.abs(row - (rows - 1) / 2) / ((rows - 1) / 2);
      const rowZ = 1 - rowDistFromCenter * 0.7;

      for (let col = 0; col < cols; col++) {
        const card = validCards[cardIndex % validCards.length];
        cardIndex++;

        const colDistFromCenter = Math.abs(col - (cols - 1) / 2) / ((cols - 1) / 2);
        const colZ = 1 - colDistFromCenter * 0.8;
        const z = Math.pow(rowZ * colZ, 0.8);

        const xPx = col * cardWidth + rowOffsetPx;
        const yPx = row * rowHeight;

        const normalizedCol = (col - (cols - 1) / 2) / ((cols - 1) / 2);
        const normalizedRow = (row - (rows - 1) / 2) / ((rows - 1) / 2);
        const tileRotateY = normalizedCol * -15;
        const tileRotateX = normalizedRow * 10;

        result.push({
          card,
          row,
          col,
          x: xPx,
          y: yPx,
          z,
          rotateX: tileRotateX,
          rotateY: tileRotateY,
          cardWidth,
          cardHeight,
        });
      }
    }

    return result.sort((a, b) => a.z - b.z);
  }, [layout, validCards]);

  if (wallCards.length === 0) {
    return null;
  }

  const gridWidth = layout.cols * layout.cardWidth + layout.cardWidth / 2;
  const gridHeight = layout.rows * (layout.cardWidth / (48 / 67));

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      width="100vw"
      height="100%"
      overflow="hidden"
      pointerEvents="none"
      zIndex={0}
    >
      <style>
        {`
          @keyframes riftboundCardFloat {
            0%, 100% { transform: var(--base-transform) translateY(0px); }
            50% { transform: var(--base-transform) translateY(-12px); }
          }
        `}
      </style>

      <Box
        ref={outerRef}
        position="absolute"
        top="50%"
        left="50%"
        width="150%"
        height="150%"
        style={{
          transform: 'translate(-50%, -50%) translate(var(--wall-offset-x, 0px), var(--wall-offset-y, 0px))',
          perspective: '900px',
          perspectiveOrigin: '50% 40%',
          transition: 'transform 0.3s ease-out',
          willChange: 'transform',
        }}
      >
        <Box
          ref={planeRef}
          position="absolute"
          top="50%"
          left="50%"
          style={{
            transformStyle: 'preserve-3d',
            transform: 'translate(-50%, -50%) rotateX(var(--wall-rotate-x, 20deg)) rotateY(var(--wall-rotate-y, 0deg))',
            transition: 'transform 0.3s ease-out',
            willChange: 'transform',
            width: `${gridWidth}px`,
            height: `${gridHeight}px`,
          }}
        >
          {wallCards.map(({ card, row, col, x, y, z, rotateX, rotateY, cardWidth, cardHeight }, index) => {
            const cardId = getCardId(card) || String(index);
            const imageUrl = cardImageUrl(card);

            const blur = layout.blur ? Math.max(0, (1 - z) * 5) : 0;
            const opacity = 0.1 + z * 0.6;
            const scale = 0.5 + z * 0.6;
            const zOffset = (z - 0.5) * 200;

            const animDuration = 6;
            const animDelay = row * 0.3 + col * 0.2;

            const cssVars = {
              '--base-transform': `translateZ(${zOffset}px) scale(${scale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
            } as CSSProperties;

            return (
              <Box
                key={`wall-${cardId}-${row}-${col}`}
                position="absolute"
                left={`${x}px`}
                top={`${y}px`}
                width={`${cardWidth}px`}
                height={`${cardHeight}px`}
                padding="5px"
                style={{
                  ...cssVars,
                  transform: `translateZ(${zOffset}px) scale(${scale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
                  opacity,
                  filter: blur > 0.5 ? `blur(${blur}px)` : 'none',
                  animation: layout.float ? `riftboundCardFloat ${animDuration}s ease-in-out infinite` : undefined,
                  animationDelay: layout.float ? `${animDelay}s` : undefined,
                  transformOrigin: 'center center',
                }}
              >
                <Box
                  width="100%"
                  height="100%"
                  borderRadius="10px"
                  overflow="hidden"
                  bg="bgElevated"
                  css={{
                    boxShadow: `0 ${8 + z * 20}px ${15 + z * 35}px rgba(0, 0, 0, ${0.4 + z * 0.4})`,
                  }}
                >
                  <img
                    src={sanityCardArtUrl(imageUrl, { width: layout.imageWidth, format: 'webp' })}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    loading="lazy"
                    draggable={false}
                    onError={() => handleImageError(cardId)}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        position="absolute"
        inset={0}
        pointerEvents="none"
        zIndex={1}
        style={{ background: vignetteGradient }}
      />
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        height="120px"
        pointerEvents="none"
        zIndex={1}
        style={{ background: topFadeGradient }}
      />
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height="180px"
        pointerEvents="none"
        zIndex={1}
        style={{ background: bottomFadeGradient }}
      />
    </Box>
  );
}
